'use client';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeIn } from '@/lib/animations';
import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

const faqs = [
  {
    question: "What is a food grade warehouse?",
    answer: "A food grade warehouse is a storage facility certified to handle food products safely. It follows strict guidelines for cleanliness, temperature control, pest management, and regulatory compliance to ensure the integrity and safety of consumable goods."
  },
  {
    question: "Why is food grade storage important?",
    answer: "Proper food grade storage prevents contamination, spoilage, and regulatory violations. Certified warehouses ensure food safety standards are met, protecting product quality and consumer health while minimizing business risk."
  },
  {
    question: "What certifications are required for food grade warehousing?",
    answer: "Certified food grade warehouses typically comply with FDA, USDA, FSMA regulations, and follow Good Manufacturing Practices (GMP) and HACCP protocols. Regular audits and inspections maintain certification status."
  },
  {
    question: "What types of products can be stored in a food grade warehouse?",
    answer: "Common products include packaged foods, beverages, dry goods, raw ingredients, refrigerated items, and frozen goods. The warehouse must meet sanitation and temperature standards suitable for each product type."
  },
  {
    question: "What's the difference between a regular warehouse and a food grade warehouse?",
    answer: "A food grade warehouse follows more stringent protocols than a regular warehouse, including sanitation schedules, pest control, temperature and humidity control, and compliance with food safety regulations."
  },
  {
    question: "How does temperature control work in food grade storage?",
    answer: "Temperature-controlled zones—dry, refrigerated, or freezer—are carefully monitored and maintained to prevent spoilage, extend shelf life, and ensure compliance with safety standards."
  },
  {
    question: "Do food grade warehouses offer long-term and short-term storage options?",
    answer: "Yes, reputable food grade warehouses provide flexible solutions, offering both short-term overflow storage and long-term warehousing depending on your operational needs."
  },
  {
    question: "Can small businesses benefit from food grade warehousing services?",
    answer: "Absolutely. Food grade warehousing is scalable, allowing small businesses access to compliant storage solutions without the overhead costs of maintaining their own facility."
  },
  {
    question: "How does inventory management work in a food grade warehouse?",
    answer: "Advanced Warehouse Management Systems (WMS) provide real-time inventory tracking, ensuring accurate reporting, traceability, and control over stock levels at all times."
  },
  {
    question: "Is there a certified food grade warehouse near Milwaukee or Southeastern Wisconsin?",
    answer: "Yes, there are certified food grade warehouses serving the Milwaukee area and Southeastern Wisconsin, offering compliant storage solutions for businesses of all sizes."
  }
];

export function FoodGradeFAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.05,
    rootMargin: '200px 0px',
  });

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-24 bg-gray-50">
      <div className="container mx-auto px-4 md:px-6">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="text-center mb-16"
        >
          <h2 className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-2">
            Frequently Asked Questions
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Food Grade Warehouse FAQs
          </h3>
          <p className="max-w-2xl mx-auto text-gray-700">
            Get answers to common questions about food grade warehousing and our certified storage solutions.
          </p>
        </motion.div>

        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "show" : "hidden"}
          variants={fadeIn('up')}
          className="max-w-4xl mx-auto"
        >
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                variants={fadeIn('up', 0.05 * index)}
                className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <h4 className="text-lg font-semibold text-gray-900 pr-4">
                    {faq.question}
                  </h4>
                  {openIndex === index ? (
                    <ChevronUp className="text-brand-600 flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-brand-600 flex-shrink-0" size={24} />
                  )}
                </button>
                
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="px-8 pb-6 pt-4"
                  >
                    <p className="text-gray-700 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}